import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api.js';
import { lifecyclePhase, PHASE } from '../lib/lifecycle-notice.js';
import StatusBadge from '../components/StatusBadge.jsx';
import { fleetStatusSummary, runStatusLabel, runStatusTone } from '../lib/status-labels.js';
import { customerSectionPath } from '../lib/navigation.js';
import { timeAgo } from '../lib/customer-format.js';
import FleetKpis from '../components/FleetKpis.jsx';
import { SCORE_UNIT } from '../lib/score-format.js';
import KpiCard, { KpiGrid } from '../components/KpiCard.jsx';
import PageHeader from '../components/PageHeader.jsx';
import PageState from '../components/PageState.jsx';
import SectionCard from '../components/SectionCard.jsx';
import Notice from '../components/Notice.jsx';
import Dialog, { DialogError } from '../components/Dialog.jsx';
import FleetStatusTable from '../components/FleetStatusTable.jsx';
import { behindCustomerIds } from '../../../shared/fleet-status.mjs';

// GET /customers pages with a cursor; the fleet view reads every page (a guard stops a cursor that never ends).
const MAX_PAGES = 100;
// The most GET /customers returns per page: fewer round trips for a fleet-wide read.
const FLEET_PAGE_LIMIT = 200;

/**
 * The operators' fleet overview (/fleet, portal workbench). Customer users
 * never reach it (components/RequireOperator.jsx); their "/" is
 * pages/Landing.jsx.
 */
export default function Dashboard() {
  // ENTRAID-347 design language: one PageHeader that names the page, never "Welcome, name".
  return (
    <div>
      <PageHeader title="Fleet overview" subline="Every customer deployment: who needs attention, credential alerts and collection failures." />
      <OperatorDashboard />
    </div>
  );
}

function OperatorDashboard() {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pushBusy, setPushBusy] = useState(false);
  const [pushResult, setPushResult] = useState(null);
  // Fleet status meta (GET /customers, operators only): the latest collector code key.
  const [latestKey, setLatestKey] = useState(null);
  const [confirmBehind, setConfirmBehind] = useState(false);
  const [behindError, setBehindError] = useState(null);

  async function loadAll() {
    const all = [];
    let meta = null;
    let cursor = null;
    let pages = 0;
    do {
      const r = await api.listCustomers(cursor, FLEET_PAGE_LIMIT);
      all.push(...(r?.items ?? []));
      meta = r?.fleet ?? meta;
      cursor = r?.cursor ?? null;
      pages += 1;
    } while (cursor && pages < MAX_PAGES);
    return { all, meta };
  }

  function load({ quiet = false } = {}) {
    if (!quiet) setLoading(true);
    setError(null);
    return loadAll()
      .then(({ all, meta }) => {
        setCustomers(all);
        setLatestKey(typeof meta?.collectorCodeKey === 'string' ? meta.collectorCodeKey : null);
      })
      .catch((e) => setError(e?.message || 'The fleet list could not be loaded.'))
      .finally(() => setLoading(false));
  }

  useEffect(() => { load(); }, []);

  if (loading) return <PageState state="loading" header={false} cards={5} label="Loading fleet data" />;
  if (error) {
    return <PageState state="error" title="The fleet list could not be loaded" message={error} onRetry={load} />;
  }

  // Grouped by the same lifecycle phases the customer tabs use (ENTRAID-259).
  const phaseOf = (c) => lifecyclePhase(c.status, { hasData: true });
  const active = customers.filter((c) => phaseOf(c) === PHASE.READY);
  const decommissioned = customers.filter((c) => phaseOf(c) === PHASE.ENDED);
  // One tile per customer in its status color and one caption line per non-active status (product owner, 2026-10-22).
  const fleet = fleetStatusSummary(customers);

  const now = Date.now();
  const credWarnings = customers.filter((c) => {
    if (!c.appCredentialExpiresAt) return false;
    const exp = new Date(c.appCredentialExpiresAt).getTime();
    return exp < now + 30 * 86400000;
  });
  const credExpired = credWarnings.filter((c) => new Date(c.appCredentialExpiresAt).getTime() < now);
  const credExpiringSoon = credWarnings.filter((c) => new Date(c.appCredentialExpiresAt).getTime() >= now);

  const scores = customers.map((c) => c.securityScore).filter((v) => typeof v === 'number' && Number.isFinite(v));
  const avgScore = scores.length > 0 ? Math.round(scores.reduce((a, b) => a + b, 0) / scores.length) : null;

  const recentFailures = customers.filter((c) => {
    if (!c.lastRunStatusCode) return false;
    return c.lastRunStatusCode >= 207;
  });

  const behindIds = behindCustomerIds(customers, latestKey);
  const behindCustomers = customers.filter((c) => behindIds.includes(c.customerId));

  async function handleBehindPush() {
    setPushBusy(true); setBehindError(null); setPushResult(null);
    try {
      const res = await api.fleetPushCode({ customerIds: behindIds });
      setPushResult(res);
      setConfirmBehind(false);
      // The table reads the push outcome from the records: refresh them without the loading skeleton.
      load({ quiet: true });
    } catch (e) {
      setBehindError(e?.message || 'The push could not be started. Try again.');
    } finally {
      setPushBusy(false);
    }
  }

  async function handleFleetPush() {
    setPushBusy(true); setPushResult(null);
    try {
      const res = await api.fleetPushCode();
      setPushResult(res);
    } catch (e) {
      setPushResult({ failed: 1, errors: [{ error: e.message }] });
    } finally {
      setPushBusy(false);
    }
  }

  return (
    <div className="space-y-6">
      {/* Fleet health KPI cards (ENTRAID-347, components/KpiCard.jsx) */}
      <KpiGrid max={5}>
        <KpiCard label="Total customers" value={customers.length} caption={decommissioned.length > 0 ? `${decommissioned.length} decommissioned` : null} />
        <KpiCard
          label="Active"
          value={active.length}
          unit={`of ${customers.length}`}
          variant="tiles"
          tileStates={fleet.tiles}
          ariaLabel={`Active: ${active.length} of ${customers.length} customers${fleet.groups.map((g) => `, ${g.count} ${g.label}`).join('')}`}
          caption={<FleetStatusCaption groups={fleet.groups} />}
        />
        <KpiCard
          label="Avg onGuard score"
          value={avgScore}
          unit={SCORE_UNIT}
          variant="donut"
          percent={avgScore}
          tone={avgScore == null ? undefined : avgScore >= 80 ? 'good' : avgScore >= 60 ? 'warn' : 'bad'}
          caption={(() => {
            const low = scores.filter((s) => s < 60).length;
            return low > 0 ? `${low} below 60%` : scores.length > 0 ? 'all at 60% or above' : 'no data yet';
          })()}
        />
        <KpiCard
          label="Credential warnings"
          value={credWarnings.length}
          tone={credExpired.length > 0 ? 'bad' : credExpiringSoon.length > 0 ? 'warn' : 'good'}
          caption={credExpired.length > 0 ? `${credExpired.length} expired` : credExpiringSoon.length > 0 ? 'expiring within 30d' : 'all OK'}
        />
        <KpiCard
          label="Run failures"
          value={recentFailures.length}
          tone={recentFailures.length > 0 ? 'bad' : 'good'}
          caption={recentFailures.length > 0 ? 'needs attention' : 'all healthy'}
        />
      </KpiGrid>

      {/* Fleet actions */}
      <SectionCard
        title="Fleet operations"
        description={`Update all ${active.length} active customer collectors to the latest version.`}
        action={(
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              className="btn-secondary"
              onClick={() => { setBehindError(null); setConfirmBehind(true); }}
              disabled={pushBusy || behindIds.length === 0}
              data-testid="push-behind"
            >
              {`Push to the ${behindIds.length} behind`}
            </button>
            <button
              type="button"
              className="btn-primary"
              onClick={handleFleetPush}
              disabled={pushBusy || active.length === 0}
            >
              {pushBusy ? 'Updating...' : 'Update all collectors'}
            </button>
          </div>
        )}
      >
        {pushResult ? (
          <Notice
            tone={pushResult.failed > 0 ? 'warn' : 'good'}
            title={pushResult.failed > 0 ? 'Some collectors were not updated' : 'Collectors updated'}
            titleAs="h3"
            testId="fleet-push-result"
          >
            {pushResult.updated > 0 && <span>{pushResult.updated} updated</span>}
            {pushResult.alreadyCurrent > 0 && <span>{pushResult.updated > 0 ? ', ' : ''}{pushResult.alreadyCurrent} already current</span>}
            {pushResult.failed > 0 && <span> &middot; {pushResult.failed} failed</span>}
            {pushResult.errors?.length > 0 && (
              <ul className="mt-1 text-xs text-ipc-ink">
                {pushResult.errors.map((e, i) => <li key={i}>{e.customerId}: {e.error}</li>)}
              </ul>
            )}
          </Notice>
        ) : null}
      </SectionCard>

      <Dialog
        open={confirmBehind}
        title={`Push the latest collector to ${behindIds.length} ${behindIds.length === 1 ? 'customer' : 'customers'}`}
        onClose={() => { if (!pushBusy) setConfirmBehind(false); }}
        footer={(
          <>
            <button type="button" className="btn-secondary" onClick={() => setConfirmBehind(false)} disabled={pushBusy}>Cancel</button>
            <button type="button" className="btn-primary" onClick={handleBehindPush} disabled={pushBusy || behindIds.length === 0}>
              {pushBusy ? 'Pushing...' : `Push to ${behindIds.length}`}
            </button>
          </>
        )}
      >
        <p>These collectors run an older version. The push updates their collector code only; settings and data sources stay as they are. Customers whose last push failed are not included: retry those separately.</p>
        <ul className="mt-2 list-disc pl-5" data-testid="push-behind-list">
          {behindCustomers.map((c) => <li key={c.customerId}>{c.companyName || c.customerId}</li>)}
        </ul>
        <DialogError message={behindError} />
      </Dialog>

      {/* Fleet status (trust stack, collector, last run per customer) */}
      <SectionCard
        title="Fleet status"
        description="Every customer's trust stack, collector and last run, so you can see who needs a push."
        testId="fleet-status"
      >
        <FleetStatusTable customers={customers} latestKey={latestKey} />
      </SectionCard>

      {/* Credential warnings detail */}
      {credWarnings.length > 0 && (
        <SectionCard title="Credential alerts" description="Customer app credentials expired or expiring within 30 days." testId="credential-alerts">
          <ul className="divide-y divide-ipc-border">
            {credWarnings.map((c) => {
              const exp = new Date(c.appCredentialExpiresAt);
              const expired = exp.getTime() < now;
              return (
                <li key={c.customerId} className="flex items-center justify-between gap-3 py-2.5 text-sm">
                  <Link to={`${customerSectionPath(c.customerId, 'settings')}/aws-deployment`} className="font-medium text-ipc-light hover:underline">
                    {c.companyName || c.customerId}
                  </Link>
                  <span className={expired ? 'badge-red' : 'badge-amber'}>
                    {expired ? `expired ${exp.toLocaleDateString()}` : `expires ${exp.toLocaleDateString()}`}
                  </span>
                </li>
              );
            })}
          </ul>
        </SectionCard>
      )}

      {/* Recent run failures */}
      {recentFailures.length > 0 && (
        <SectionCard title="Recent collection failures" description="Customers whose last run had partial or full failures." testId="collection-failures">
          <ul className="divide-y divide-ipc-border">
            {recentFailures.map((c) => (
              <li key={c.customerId} className="flex items-center justify-between gap-3 py-2.5 text-sm">
                <Link to={customerSectionPath(c.customerId, 'data-collection')} className="font-medium text-ipc-light hover:underline">
                  {c.companyName || c.customerId}
                </Link>
                <span className={`badge-${runStatusTone(c.lastRunStatusCode)}`}>
                  {runStatusLabel(c.lastRunStatusCode)}
                </span>
              </li>
            ))}
          </ul>
        </SectionCard>
      )}

      {/* Recent customers quick list */}
      <SectionCard title="Customers" action={<Link to="/customers" className="text-ipc-light hover:underline">View all</Link>}>
        {customers.length > 0 ? (
          <div className="divide-y divide-ipc-border">
            {customers.slice(0, 8).map((c) => (
              <Link
                key={c.customerId}
                to={customerSectionPath(c.customerId, 'overview')}
                className="-mx-2 flex min-h-10 items-center justify-between rounded px-2 py-3 transition-colors hover:bg-ipc-surface"
              >
                <div className="min-w-0">
                  <div className="text-sm font-medium text-ipc-ink">{c.companyName || c.customerId}</div>
                  <div className="font-mono text-xs text-ipc-muted">{c.customerId}</div>
                </div>
                <div className="flex flex-wrap items-center justify-end gap-3">
                  <FleetKpis customer={c} />
                  <StatusBadge status={c.status} />
                  {c.lastRunAt && (
                    <span className="text-xs text-ipc-muted">
                      {timeAgo(c.lastRunAt)}
                    </span>
                  )}
                </div>
              </Link>
            ))}
          </div>
        ) : (
          <p className="text-sm text-ipc-muted">No customers yet. They appear here once a customer buys onGuard or an operator adds one.</p>
        )}
      </SectionCard>
    </div>
  );
}

// Status dots for the Active card's caption: caution amber and bad red (dots only; the words stay ink).
const FLEET_DOT = Object.freeze({ warn: 'var(--warn-dot)', bad: 'var(--bad)' });

/** "1 registered", "1 deployment failed": one line per non-active status with its own dot; never green for pending. */
function FleetStatusCaption({ groups }) {
  if (groups.length === 0) {
    return <span className="inline-flex items-center gap-2 text-ipc-ink"><span className="block h-2.5 w-2.5 shrink-0 rounded-sm" style={{ background: 'var(--good)' }} aria-hidden="true" />All customers active</span>;
  }
  return (
    <ul className="space-y-1" data-testid="fleet-status-groups">
      {groups.map((g) => (
        <li key={g.label} className="flex items-center gap-2 text-ipc-ink" data-tone={g.tone}>
          <span className="block h-2.5 w-2.5 shrink-0 rounded-sm" style={{ background: FLEET_DOT[g.tone] }} aria-hidden="true" />
          <span><span className="font-semibold tabular-nums">{g.count}</span> {g.label}</span>
        </li>
      ))}
    </ul>
  );
}
